import {
  Box,
  Button,
  CircularProgress,
  SxProps,
  Typography,
  circularProgressClasses,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { showRemainingTime, useCountdown } from '../hooks';

interface LoadingProps {
  ETA?: number;
  onCountedDown?: () => void;
  onCancel?: () => void;
  header?: string;
  sx?: SxProps;
}

function Loading({
  ETA,
  onCountedDown,
  onCancel,
  header,
  sx,
}: LoadingProps): JSX.Element {
  const countDown = useCountdown(ETA, onCountedDown);
  const { t } = useTranslation('translations'); //make sure common namespace is used
  const defaultHeader = header
    ? header
    : t('common.loading.title', { defaultValue: 'Loading...' });

  return (
    <Box
      display="flex"
      height="100%"
      width="100%"
      flexDirection={'column'}
      bgcolor="white"
      position={'absolute'}
      top={0}
      left={0}
      zIndex={100}
      data-testid="loading-indicator"
      alignItems="center"
      paddingTop={'84px'}
      gap={'102px'}
      sx={sx}
    >
      <Typography fontSize={'36px'}>{defaultHeader}</Typography>
      <Box
        display="flex"
        style={{ flexDirection: 'column', alignItems: 'center', gap: '16px' }}
      >
        <Box sx={{ position: 'relative' }}>
          <CircularProgress
            variant="determinate"
            sx={(theme) => ({
              color: theme.palette.primary.main,
              stroke: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
            })}
            size={150}
            thickness={10}
            value={100}
          />
          <CircularProgress
            variant="indeterminate"
            disableShrink
            sx={(theme) => ({
              color: theme.palette.primary.light,
              stroke: theme.palette.primary.light,
              borderColor: theme.palette.primary.light,
              animationDuration: '2500ms',
              position: 'absolute',
              left: 0,
              [`& .${circularProgressClasses.circle}`]: {
                strokeLinecap: 'butt',
              },
            })}
            size={150}
            thickness={10}
          />
        </Box>
        <Typography variant="subtitle1">
          {ETA
            ? countDown <= 0
              ? t('common.loading.wait', {
                  defaultValue: 'Please wait...',
                })
              : showRemainingTime(t, countDown)
            : ''}
        </Typography>
        {ETA && (
          <Button
            variant="outlined"
            onClick={() => onCancel?.()}
            sx={(theme) => ({
              borderRadius: '50px',
              borderColor: theme.palette.primary.light,
              color: theme.palette.primary.light,
              '&:hover': {
                backgroundColor: theme.palette.secondary.main,
                borderColor: theme.palette.secondary.main,
              },
            })}
          >
            {t('common.loading.cancel', {
              defaultValue: 'Cancel',
            })}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export { Loading };
